import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, PenLine, RotateCcw, ShieldCheck } from "lucide-react";
import { SIG, formatSignedAt, signatureBlockLines } from "@/lib/signatureFlow";

function SignatureExample({ name }) {
  const [line1, line2] = signatureBlockLines(name);
  return (
    <div className="mt-2 rounded-xl bg-white border border-slate-200 p-3 font-mono text-sm text-slate-900 break-words" data-testid="mail-signature-preview">
      <div>{line1}</div>
      <div>{line2}</div>
    </div>
  );
}

function StatusNotice({ sigState, onRetryStatus }) {
  if (sigState === SIG.CHECKING) {
    return (
      <div className="p-4 text-xs text-slate-600 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-2" data-testid="mail-signature-checking">
        <Loader2 className="animate-spin" size={14} />Checking signature status…
      </div>
    );
  }
  return (
    <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-red-800 text-sm" data-testid="mail-signature-status-error">
      We couldn't check your signature status.
      <Button variant="outline" onClick={onRetryStatus} className="mt-3 rounded-xl min-h-[44px] w-full sm:w-auto" data-testid="mail-signature-status-retry">
        <RotateCcw size={14} className="mr-1" />Retry
      </Button>
    </div>
  );
}

export function SignatureAuthorizationPanel({ sigState, signatureName, profileNameOk, consentChecked, onConsentChange, onApply, onRetryStatus, signError }) {
  if (sigState === SIG.CHECKING || sigState === SIG.STATUS_ERROR) {
    return <StatusNotice sigState={sigState} onRetryStatus={onRetryStatus} />;
  }
  const signing = sigState === SIG.SIGNING;
  return (
    <div className="rounded-2xl border border-purple-200 bg-purple-50/60 p-4" data-testid="mail-signature-panel">
      <div className="flex items-center gap-2 font-semibold text-slate-900">
        <PenLine size={16} className="text-purple-600" />Electronic Signature Authorization
      </div>
      {!profileNameOk ? (
        <div className="mt-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="mail-signature-missing-name">
          Your legal first and last name must be on your profile before you can authorize a signature. Please update your profile and reopen this dialog.
        </div>
      ) : (
        <>
          <p className="mt-2 text-sm text-slate-700">Before we build your final mailing packet, authorize your typed electronic signature. Legal name on file:</p>
          <div className="mt-1 text-base font-semibold text-slate-900 break-words" data-testid="mail-signature-legal-name">{signatureName}</div>
          <p className="mt-3 text-xs text-slate-600">Your signature will appear on the letter exactly as:</p>
          <SignatureExample name={signatureName} />
          <label className="mt-3 flex items-start gap-3 text-sm text-slate-800 min-h-[44px] p-2 -mx-2 rounded-xl cursor-pointer" data-testid="mail-signature-authorize-label">
            <Checkbox
              checked={consentChecked}
              onCheckedChange={(v) => onConsentChange(!!v)}
              disabled={signing}
              className="h-6 w-6 mt-0.5 flex-shrink-0"
              data-testid="mail-signature-authorize"
            />
            <span>I authorize Informed Accelerator to apply my typed electronic signature, <b>/s/ {signatureName}</b>, to this dispute letter.</span>
          </label>
          <Button onClick={onApply} disabled={!consentChecked || signing} className="btn-primary rounded-xl min-h-[44px] mt-3 w-full sm:w-auto" data-testid="mail-signature-apply">
            {signing ? <><Loader2 className="animate-spin mr-2" size={14} />Applying signature…</> : <><ShieldCheck size={14} className="mr-1" />Apply Signature and Generate Preview</>}
          </Button>
        </>
      )}
      {signError && (
        <div className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 text-xs text-red-900" role="alert" data-testid="mail-signature-error">
          {signError}
          {sigState === SIG.SIGN_ERROR && (
            <Button variant="outline" onClick={onApply} disabled={!consentChecked} className="mt-2 rounded-xl min-h-[44px] w-full sm:w-auto" data-testid="mail-signature-retry">
              <RotateCcw size={14} className="mr-1" />Retry
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function SignedConfirmation({ name, signedAt, testid = "mail-signature-confirmation" }) {
  return (
    <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start gap-2" data-testid={testid}>
      <ShieldCheck size={16} className="text-emerald-700 mt-0.5 flex-shrink-0" />
      <span>Electronically signed by <b>{name}</b> on <time dateTime={signedAt}>{formatSignedAt(signedAt)}</time>.</span>
    </div>
  );
}
